import {
  type UseQueryResult,
  useMutation,
  useQuery,
} from "@tanstack/react-query"
import axios, { type AxiosResponse } from "axios"
import SessionStorage from "~/lib/storage/SessionStorage"

type SquidexAuthResult = {
  access_token: string
  expires_in: number
}

type SquidexConfig = {
  host: string
  appId: string
  appScope: string
  clientSecret: string
}

const squidexConfig: SquidexConfig = {
  host: process.env.GATSBY_REST_SQUIDEX_BASE_URI ?? "",
  appId: process.env.GATSBY_REST_SQUIDEX_SPACE_ID ?? "",
  appScope: process.env.GATSBY_REST_SQUIDEX_APP_SCOPE ?? "",
  clientSecret: process.env.GATSBY_REST_SQUIDEX_CLIENT_SECRET ?? "",
}

const squidexApi = axios.create({
  baseURL: squidexConfig.host,
})

function getAxiosToken(): Promise<string> {
  return new Promise((resolve) => {
    const cachedToken = SessionStorage.getSerialized<{ token?: string | null }>(
      "squidexToken",
      { token: null }
    )

    if (
      !squidexConfig.host ||
      !squidexConfig.appId ||
      !squidexConfig.clientSecret
    ) {
      resolve("")
      return
    }

    if (cachedToken.token) {
      return resolve(cachedToken.token)
    }

    axios
      .post<SquidexAuthResult>(
        `${squidexConfig.host}/identity-server/connect/token`,
        `grant_type=client_credentials&client_id=${squidexConfig.appId}:${squidexConfig.appScope}&client_secret=${squidexConfig.clientSecret}&scope=squidex-api`,
        {
          headers: { "content-type": "application/x-www-form-urlencoded" },
        }
      )
      .then((response) => {
        const token = response.data.access_token

        SessionStorage.setSerialized(
          "squidexToken",
          { token: token },
          response.data.expires_in
        )
        resolve(token)
      })
  })
}

async function squidexAuthInterceptor(config) {
  const token = await getAxiosToken()

  return {
    ...config,
    headers: {
      ...config.headers,
      Authorization: `Bearer ${token}`,
    },
  }
}

squidexApi.interceptors.request.use(squidexAuthInterceptor)

export type SquidexAssetItem = {
  id: string
  fileName: string
  mimeType: string
  fileType: string
  metadata?: {
    alt?: string | null
    title?: string | null
  }
  _links: {
    self: {
      href: string
      method: "GET"
    }
    update: {
      href: string
      method: "PUT"
    }
    content: {
      href: string
      method: "GET"
    }
  }
}

type SquidexAssetResult = {
  items: SquidexAssetItem[]
}

function fetchSquidexApiAssets({ queryKey }) {
  return squidexApi.post(`/api/apps/${squidexConfig.appId}/assets/query`, {
    ids: queryKey[1],
  })
}

export function useSquidexAssetsQuery(
  assetsIds: string[]
): UseQueryResult<AxiosResponse<SquidexAssetResult>> {
  return useQuery({
    queryKey: ["squidex-assets-query", assetsIds],
    queryFn: fetchSquidexApiAssets,
    staleTime: Infinity,
  })
}

export function useSquidexAssetMetadataMutation(id: string) {
  return useMutation({
    mutationKey: ["squidex-assets-metadata", id],
    mutationFn: (data: any) => {
      return squidexApi.put(`/api/apps/${squidexConfig.appId}/assets/${id}`, {
        metadata: data,
      })
    },
  })
}
